/**
 * useSearchPipeline — owns the flight / hotel / itinerary fetch pipeline
 * for the unified search surface (VOY-477).
 *
 * Why a dedicated hook
 * --------------------
 * Pre-VOY-477 this logic lived inline in `src/pages/search-plus.tsx`, so
 * the unified `/` route had no way to render real results without
 * either (a) duplicating ~600 LOC of fetch/orchestration or (b)
 * navigating to `/search-plus`. This hook lifts the pipeline so both
 * `LandingV6` (State B on `/`) and the legacy `search-plus.tsx` page
 * consume the same source of truth. After VOY-478 deletes
 * `/search-plus`, this hook is the only caller path.
 *
 * What it owns
 * ------------
 * - Result state: flights, hotels (current page), itinerary, hotel
 *   suggestions, "widened" hotels (when RateHawk falls back to a wider
 *   geographic radius), and hotel pagination metadata
 *   (`hotelPage`, `hotelTotal`, `hotelTotalPages`, `hotelSearchParams`).
 * - Loading flags per product (`isLoadingFlights/Hotels/Itinerary`)
 *   plus a coarse `isSearching` for the initial dispatch.
 * - Retry banner state (`hotelRetryState`) wired to RateHawk's
 *   connection-error retry loop (VOY-333).
 * - `hotelPageCache` — in-memory LRU keyed by destination/dates/guests/
 *   filters/page, 5-minute TTL.
 * - `searchedFor*` flags — what the LAST search asked for. Used by
 *   `SearchResults` to decide which tabs are visible.
 *
 * What it does NOT own
 * --------------------
 * - Filter state, sort state, view mode — those stay in `SearchResults`
 *   per the VOY-475 contract.
 * - Cart / booking / Stripe — downstream of this hook, untouched.
 * - Geolocation fallback for missing flight origin — the caller does
 *   that BEFORE calling `runSearch`, so the hook only sees fully-
 *   resolved params (keeps the hook pure and avoids coupling to the
 *   geolocation UI feedback states).
 * - Classic-form UX state (pending child-age prompt, AI/Classic tab,
 *   active query text). Those stay in `search-plus.tsx`.
 *
 * The hook accepts the caller's hotelFilters by reference on each call
 * so the cache key and the request payload always reflect the current
 * filter set. Filter state lives in the consumer (SearchResults).
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import { searchAirportCodes } from '@/lib/airport-codes'
import { searchHotelsDirectly, type RetryState } from '@/lib/hotels-api-backend'
import { performanceTracker } from '@/lib/performance'
import { saveSearch, normalizeCityName } from '@/lib/saved-searches'
import type {
  FlightResult,
  Hotel,
  ItineraryResponse,
  MultiCitySegment,
  TripType,
} from '@/types/travel'
import {
  searchFlightsViaBackend,
  generateItineraryViaBackend,
  parseItineraryResponse,
} from './pipeline/fetchers'
import {
  cleanFilters,
  removeAirportName,
  generateHotelPageCacheKey,
  trimHotelPageCache,
  calculateDuration,
  HOTEL_CACHE_TTL_MS,
  type HotelFilters,
  type HotelSearchParams,
  type HotelPageCacheEntry,
} from './pipeline/helpers'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RegionSuggestion {
  id: string | number
  name: string
  country_code?: string
  lat?: number
  lon?: number
  type?: string
}

/** Already-resolved search params. Caller has handled geolocation fallback. */
export interface RunSearchParams {
  tripType?: TripType
  origin?: string
  originName?: string
  destination?: string
  destinationName?: string
  departureDate?: string
  returnDate?: string
  segments?: MultiCitySegment[]
  travelers?: { adults?: number; children?: number; childrenAges?: number[] }
  passengers?: { adults?: number; children?: number; childrenAges?: number[] }
  cabinClass?: string
  preferences?: string[]
  imageSearch?: boolean
  imageMetadata?: {
    confidence?: number
    detectedLandmarks?: string[]
  }
  duration?: number
}

export interface RunSearchOptions {
  /** Which products to fetch for this run. */
  searchFor: { flights: boolean; hotels: boolean; itinerary: boolean }
  /** Current hotel filter state from the consumer. */
  hotelFilters?: HotelFilters
  /** For analytics: how the user got here. */
  searchMethod?: 'ai' | 'voice' | 'image' | 'classic'
  /** Raw user input for `search_history.raw_input`. */
  rawInput?: string
  /** Optional: caller can provide an AbortSignal to cancel mid-run. */
  signal?: AbortSignal
}

export interface SearchPipelineApi {
  // Result state
  flights: FlightResult[]
  hotels: Hotel[]
  itinerary: ItineraryResponse | null
  hotelSuggestions: RegionSuggestion[]
  widenedHotels: Hotel[]
  hotelTotal: number
  hotelPage: number
  hotelPerPage: number
  hotelTotalPages: number
  hotelSearchParams: HotelSearchParams
  // Loading flags
  isSearching: boolean
  isLoadingFlights: boolean
  isLoadingHotels: boolean
  isLoadingItinerary: boolean
  isGeneratingItinerary: boolean
  hotelRetryState: RetryState | null
  // What the last search asked for — drives tab visibility.
  searchedForFlights: boolean
  searchedForHotels: boolean
  searchedForItinerary: boolean
  // VOY-466: Match-My-Taste toggle state. Threaded into every
  // searchHotelsDirectly call so the backend can score + re-rank.
  // Defaults to false; LandingV6 enables it for authed users on
  // mount, MatchMyTasteToggle flips it via setPersonalizeHotels.
  // The setter also re-fetches the current hotel page so the toggle
  // takes effect immediately without waiting for a fresh search.
  personalizeHotels: boolean
  setPersonalizeHotels: (next: boolean) => void
  // VOY-461: flight "Match my taste" — parity with hotels.
  personalizeFlights: boolean
  setPersonalizeFlights: (next: boolean) => void
  // Actions
  runSearch: (params: RunSearchParams, opts: RunSearchOptions) => Promise<void>
  fetchHotelsPage: (page: number, hotelFilters?: HotelFilters) => Promise<void>
  reset: () => void
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const HOTEL_PER_PAGE = 20

export function useSearchPipeline(): SearchPipelineApi {
  const { user } = useAuth()

  // ---- Result state ----
  const [flights, setFlights] = useState<FlightResult[]>([])
  const [hotels, setHotels] = useState<Hotel[]>([])
  const [itinerary, setItinerary] = useState<ItineraryResponse | null>(null)
  const [hotelSuggestions, setHotelSuggestions] = useState<RegionSuggestion[]>([])
  const [widenedHotels, setWidenedHotels] = useState<Hotel[]>([])
  const [hotelSearchParams, setHotelSearchParams] = useState<HotelSearchParams>({})
  const [hotelTotal, setHotelTotal] = useState(0)
  const [hotelPage, setHotelPage] = useState(1)
  const [hotelTotalPages, setHotelTotalPages] = useState(0)

  // ---- Loading flags ----
  const [isSearching, setIsSearching] = useState(false)
  const [isLoadingFlights, setIsLoadingFlights] = useState(false)
  const [isLoadingHotels, setIsLoadingHotels] = useState(false)
  const [isLoadingItinerary, setIsLoadingItinerary] = useState(false)
  const [isGeneratingItinerary, setIsGeneratingItinerary] = useState(false)
  const [hotelRetryState, setHotelRetryState] = useState<RetryState | null>(null)

  // ---- VOY-466: Match-My-Taste personalization flag ----
  // Default false. LandingV6 owns the "enable on auth" behaviour
  // by calling setPersonalizeHotels(true) when `user` resolves.
  // We expose the setter via the API instead of forcing it here
  // because useSearchPipeline shouldn't depend on auth-state side
  // effects — it's a pure data pipeline. The setter is wrapped
  // with a refetch so toggling the UI takes effect immediately
  // without waiting for a fresh search.
  // The refs mirror the flags so the runSearch/fetchHotelsPage callbacks
  // read the latest value without a stale closure. They're updated inside
  // the setters (setPersonalizeHotels/Flights) — NOT written during render,
  // which avoids the Fast-Refresh hook-queue corruption that a render-time
  // ref write can trigger when hooks are edited with the page open.
  const [personalizeHotels, setPersonalizeHotelsState] = useState(false)
  const personalizeHotelsRef = useRef(false)

  // VOY-461: flight "Match my taste" — parity with hotels, driven by the
  // flight reranker. Threaded into the flight search request as `personalize`.
  const [personalizeFlights, setPersonalizeFlightsState] = useState(false)
  const personalizeFlightsRef = useRef(false)

  // ---- What the last search asked for ----
  const [searchedForFlights, setSearchedForFlights] = useState(true)
  const [searchedForHotels, setSearchedForHotels] = useState(true)
  const [searchedForItinerary, setSearchedForItinerary] = useState(true)

  // ---- In-memory hotel page cache (LRU, TTL) ----
  const hotelPageCacheRef = useRef<Map<string, HotelPageCacheEntry>>(new Map())

  // ---- Latest filters reference for cache-key generation in pagination ----
  // We can't read filters from a closure in fetchHotelsPage because it's
  // called by SearchResults independently from runSearch; the consumer
  // passes filters when calling. We track the last-used set as a fallback.
  const lastFiltersRef = useRef<HotelFilters>({})

  const reset = useCallback(() => {
    setFlights([])
    setHotels([])
    setItinerary(null)
    setHotelSuggestions([])
    setWidenedHotels([])
    setHotelSearchParams({})
    setHotelTotal(0)
    setHotelPage(1)
    setHotelTotalPages(0)
    setIsSearching(false)
    setIsLoadingFlights(false)
    setIsLoadingHotels(false)
    setIsLoadingItinerary(false)
    setIsGeneratingItinerary(false)
    setHotelRetryState(null)
    hotelPageCacheRef.current.clear()
  }, [])

  // -------------------------------------------------------------------------
  // runSearch — the orchestration. Mirrors handleDirectSearch from
  // pre-VOY-477 search-plus.tsx (lines 1091-1585) but pulled into a hook
  // so the unified `/` route can drive it without duplicating logic.
  // -------------------------------------------------------------------------
  const runSearch = useCallback(
    async (params: RunSearchParams, opts: RunSearchOptions) => {
      const { searchFor, hotelFilters = {}, searchMethod = 'ai', rawInput, signal } = opts
      lastFiltersRef.current = hotelFilters

      // Track what THIS search asked for so SearchResults can show the
      // right tabs even after the run completes.
      //
      // For Flights: KEEP the tab visible even if origin is missing.
      // Previously I hid the tab in that case (PR #280 first round),
      // but user feedback was "I don't know why flights are missing".
      // Showing the Flights tab with its existing "No flights found"
      // empty state plus a toast from LandingV6 explaining the missing
      // origin is more discoverable. The FROM pill in the EditablePillRow
      // above the results is the affordance to fix it.
      //
      // For Itinerary: still gate on departureDate. The itinerary panel's
      // empty state ("Search for flights to get a personalized travel
      // itinerary") would mislead users who searched without dates.
      const canSearchItinerary =
        !!searchFor.itinerary &&
        params.tripType !== 'multi-city' &&
        !!params.destination &&
        !!params.departureDate
      setSearchedForFlights(!!searchFor.flights)
      setSearchedForHotels(!!searchFor.hotels)
      setSearchedForItinerary(canSearchItinerary)

      setIsSearching(true)
      setFlights([])
      setHotels([])
      setItinerary(null)
      setIsGeneratingItinerary(!!searchFor.itinerary)

      const startDate = params.departureDate
      let endDate = params.returnDate

      // Normalise reversed date ranges — fall back to `duration` (or 7d)
      // so itinerary generation gets a sensible window.
      if (startDate && endDate) {
        const s = new Date(startDate)
        const e = new Date(endDate)
        if (e < s) {
          const duration = params.duration || 7
          const fixed = new Date(s)
          fixed.setDate(s.getDate() + duration)
          endDate = fixed.toISOString()
        }
      }

      // Persist to search_history + saved_searches for logged-in users.
      // Wrapped in try/catch — analytics failures must not block the search.
      if (user) {
        try {
          await supabase.from('search_history').insert({
            user_id: user.id,
            search_method: searchMethod,
            query: params,
            raw_input: rawInput ?? '',
            ...(params.imageSearch && {
              image_search: true,
              image_confidence: params.imageMetadata?.confidence,
              detected_landmarks: params.imageMetadata?.detectedLandmarks?.join(', '),
            }),
          })
        } catch (err) {
          console.error('[Pipeline] Failed to save search_history:', err)
        }
        try {
          const searchType =
            searchFor.flights && searchFor.hotels
              ? 'trip'
              : searchFor.flights
                ? 'flight'
                : 'hotel'
          const starMin =
            hotelFilters.stars && hotelFilters.stars.length > 0
              ? Math.min(...hotelFilters.stars)
              : undefined
          const budgetRange =
            hotelFilters.priceMin != null || hotelFilters.priceMax != null
              ? { min: hotelFilters.priceMin ?? 0, max: hotelFilters.priceMax ?? null }
              : undefined
          await saveSearch(user.id, searchType, params, {
            originCityName: normalizeCityName(params.originName),
            destinationCityName: normalizeCityName(
              params.destinationName ?? params.destination,
            ),
            cabinClass: params.cabinClass?.toString().toLowerCase(),
            hotelStarMin: starMin,
            budgetRange,
            searchContext: rawInput ? rawInput.slice(0, 500) : undefined,
            searchSource: searchMethod === 'classic' ? 'classic-search' : 'ai-search',
          })
        } catch (err) {
          console.error('[Pipeline] Failed to save_search profile:', err)
        }
      }

      // ------------------------------------------------------------------
      // 1) Flight search (multi-city OR one-way/round-trip)
      // ------------------------------------------------------------------
      if (
        searchFor.flights &&
        params.tripType === 'multi-city' &&
        params.segments &&
        params.segments.length >= 2
      ) {
        const duffelParams = {
          tripType: 'multi-city' as const,
          segments: params.segments.map((seg) => ({
            origin: seg.origin.length === 3 ? seg.origin : seg.origin.substring(0, 3).toUpperCase(),
            destination:
              seg.destination.length === 3
                ? seg.destination
                : seg.destination.substring(0, 3).toUpperCase(),
            // MultiCitySegment.departureDate is Date | string in the
            // types module; the Duffel endpoint wants a string.
            departureDate:
              seg.departureDate instanceof Date
                ? seg.departureDate.toISOString()
                : seg.departureDate,
          })),
          passengers: {
            adults: params.travelers?.adults ?? params.passengers?.adults ?? 1,
            children: params.travelers?.children ?? params.passengers?.children ?? 0,
          },
          cabinClass: params.cabinClass || 'economy',
          personalize: personalizeFlightsRef.current,
        }
        setIsLoadingFlights(true)
        performanceTracker.start('flight-search-multi-city')
        try {
          const results = await searchFlightsViaBackend(duffelParams)
          if (signal?.aborted) return
          setFlights(results)
          performanceTracker.end('flight-search-multi-city', true)
        } catch (err) {
          performanceTracker.end(
            'flight-search-multi-city',
            false,
            err instanceof Error ? err.message : 'flight search failed',
          )
          // Don't rethrow — let the other products continue.
        } finally {
          setIsLoadingFlights(false)
        }
      } else if (searchFor.flights && params.destination && params.departureDate) {
        // Caller has resolved origin (geolocation, fallback, etc.) before
        // calling us. We only refuse to fetch when the origin is genuinely
        // missing — that's a caller bug, not a pipeline concern.
        const flightOrigin = params.origin?.trim()
        if (flightOrigin && flightOrigin.length === 3) {
          let destinationCode = params.destination
          if (destinationCode && destinationCode.length !== 3) {
            try {
              const results = await searchAirportCodes(destinationCode)
              if (results && results.length > 0) {
                destinationCode = results[0].code
              }
            } catch {
              // fall through with the original string
            }
          }
          const duffelParams = {
            tripType: (params.tripType || 'round-trip') as TripType,
            origin: flightOrigin.toUpperCase(),
            destination: destinationCode?.toUpperCase() || params.destination,
            departureDate: params.departureDate,
            returnDate: params.tripType === 'round-trip' ? params.returnDate : undefined,
            passengers: {
              adults: params.travelers?.adults ?? params.passengers?.adults ?? 1,
              children: params.travelers?.children ?? params.passengers?.children ?? 0,
            },
            cabinClass: params.cabinClass || 'economy',
            personalize: personalizeFlightsRef.current,
          }
          setIsLoadingFlights(true)
          performanceTracker.start('flight-search')
          try {
            const results = await searchFlightsViaBackend(duffelParams)
            if (signal?.aborted) return
            setFlights(results)
            performanceTracker.end('flight-search', true)
          } catch (err) {
            performanceTracker.end(
              'flight-search',
              false,
              err instanceof Error ? err.message : 'flight search failed',
            )
            console.error('[Pipeline] Flight search failed:', err)
          } finally {
            setIsLoadingFlights(false)
          }
        } else {
          console.log('[Pipeline] Skipping flight search: missing origin')
        }
      }

      // VOY-35 progressive loading: drop coarse `isSearching` once flights
      // are dispatched so per-product spinners take over.
      setIsSearching(false)

      // ------------------------------------------------------------------
      // 2) Hotels + 3) Itinerary fire in parallel (same as pre-VOY-477).
      // ------------------------------------------------------------------
      const pending: Array<Promise<unknown>> = []

      if (searchFor.hotels && params.destination) {
        setIsLoadingHotels(true)
        pending.push(
          (async () => {
            performanceTracker.start('hotel-search')
            try {
              const hotelSearchData = await buildHotelSearchData(params)
              setHotelRetryState(null)
              const resp = await searchHotelsDirectly(
                {
                  ...hotelSearchData,
                  page: 1,
                  per_page: HOTEL_PER_PAGE,
                  filters: cleanFilters(hotelFilters),
                  // VOY-466: thread Match-My-Taste flag. Backend
                  // no-ops on `false` and on anonymous callers.
                  personalize: personalizeHotelsRef.current,
                },
                (state) => setHotelRetryState(state),
              )
              if (signal?.aborted) return
              setHotelSuggestions(resp.suggestions ?? [])
              setWidenedHotels(resp.widened ?? [])
              setHotels(resp.hotels ?? [])
              setHotelSearchParams(hotelSearchData)
              setHotelPage(1)
              const total = resp.meta?.total ?? resp.hotels?.length ?? 0
              const totalPages = resp.meta?.total_pages ?? 0
              setHotelTotal(total)
              setHotelTotalPages(totalPages)
              setHotelRetryState(null)
              const cacheKey = generateHotelPageCacheKey(hotelSearchData, hotelFilters, 1)
              if (cacheKey && resp.hotels) {
                hotelPageCacheRef.current.set(cacheKey, {
                  hotels: resp.hotels,
                  meta: { total, total_pages: totalPages },
                  timestamp: Date.now(),
                })
                trimHotelPageCache(hotelPageCacheRef.current)
              }
              performanceTracker.end('hotel-search', true)
            } catch (err) {
              setHotels([])
              setHotelSuggestions([])
              setWidenedHotels([])
              setHotelRetryState(null)
              performanceTracker.end(
                'hotel-search',
                false,
                err instanceof Error ? err.message : 'hotel search failed',
              )
              console.error('[Pipeline] Hotel search failed:', err)
            } finally {
              setIsLoadingHotels(false)
            }
          })(),
        )
      }

      if (
        searchFor.itinerary &&
        params.tripType !== 'multi-city' &&
        params.destination &&
        startDate
      ) {
        setIsLoadingItinerary(true)
        pending.push(
          (async () => {
            performanceTracker.start('itinerary-generation')
            try {
              let itinDest = params.destinationName || params.destination
              if (params.destination && params.destination.length === 3) {
                try {
                  const results = await searchAirportCodes(params.destination)
                  if (results && results.length > 0) {
                    itinDest = removeAirportName(results[0].city)
                  }
                } catch {
                  itinDest = params.destinationName || params.destination
                }
              }
              if (itinDest) itinDest = removeAirportName(itinDest)
              const raw = await generateItineraryViaBackend({
                destination: itinDest!,
                startDate: startDate.split('T')[0],
                endDate: endDate ? endDate.split('T')[0] : undefined,
                duration: endDate
                  ? calculateDuration(new Date(startDate), new Date(endDate))
                  : undefined,
                preferences: params.preferences,
              })
              if (signal?.aborted) return
              setItinerary(parseItineraryResponse(raw))
              performanceTracker.end('itinerary-generation', true)
            } catch (err) {
              performanceTracker.end(
                'itinerary-generation',
                false,
                err instanceof Error ? err.message : 'itinerary failed',
              )
              console.error('[Pipeline] Itinerary failed:', err)
            } finally {
              setIsLoadingItinerary(false)
              setIsGeneratingItinerary(false)
            }
          })(),
        )
      } else if (searchFor.itinerary) {
        // No itinerary fetch fired — clear the "generating" flag so the
        // SearchResults shell doesn't show a spinner forever.
        setIsGeneratingItinerary(false)
      }

      if (pending.length > 0) {
        await Promise.allSettled(pending)
      }
    },
    [user],
  )

  // -------------------------------------------------------------------------
  // fetchHotelsPage — direct-paging variant called by HotelTabContent.
  // Same cache + retry semantics as runSearch's hotel branch.
  // -------------------------------------------------------------------------
  const fetchHotelsPage = useCallback(
    async (page: number, hotelFilters: HotelFilters = lastFiltersRef.current) => {
      if (
        !hotelSearchParams.destination ||
        !hotelSearchParams.checkin ||
        !hotelSearchParams.checkout ||
        !hotelSearchParams.guests
      )
        return
      lastFiltersRef.current = hotelFilters

      const cacheKey = generateHotelPageCacheKey(hotelSearchParams, hotelFilters, page)
      if (cacheKey) {
        const cached = hotelPageCacheRef.current.get(cacheKey)
        if (cached && Date.now() - cached.timestamp < HOTEL_CACHE_TTL_MS) {
          setHotels(cached.hotels)
          setHotelPage(page)
          setHotelTotal(cached.meta.total)
          setHotelTotalPages(cached.meta.total_pages)
          return
        }
        if (cached) {
          hotelPageCacheRef.current.delete(cacheKey)
        }
      }

      setIsLoadingHotels(true)
      setHotelRetryState(null)
      try {
        const resp = await searchHotelsDirectly(
          {
            // Guarded above — these are non-null by the time we get here.
            destination: hotelSearchParams.destination!,
            checkin: hotelSearchParams.checkin!,
            checkout: hotelSearchParams.checkout!,
            guests: hotelSearchParams.guests!,
            page,
            per_page: HOTEL_PER_PAGE,
            filters: cleanFilters(hotelFilters),
            // VOY-466: thread Match-My-Taste flag (see runSearch above).
            personalize: personalizeHotelsRef.current,
          },
          (state) => setHotelRetryState(state),
        )
        const fresh = resp.hotels ?? []
        const meta = {
          total: resp.meta?.total ?? 0,
          total_pages: resp.meta?.total_pages ?? 0,
        }
        setHotels(fresh)
        setHotelPage(page)
        setHotelTotal(meta.total)
        setHotelTotalPages(meta.total_pages)
        if (cacheKey) {
          hotelPageCacheRef.current.set(cacheKey, {
            hotels: fresh,
            meta,
            timestamp: Date.now(),
          })
          trimHotelPageCache(hotelPageCacheRef.current)
        }
      } catch (err) {
        console.error('[Pipeline] fetchHotelsPage failed:', err)
      } finally {
        setIsLoadingHotels(false)
        setHotelRetryState(null)
      }
    },
    [hotelSearchParams],
  )

  // VOY-466: setter that updates state AND immediately re-fetches the
  // current hotel page so the toggle feels live. Bypasses the cache —
  // a previously-cached page from the opposite personalize value
  // would be stale on every key field (sort, match_score, …).
  const setPersonalizeHotels = useCallback(
    (next: boolean) => {
      setPersonalizeHotelsState(next)
      personalizeHotelsRef.current = next
      // Only re-fetch when there's a live search to refresh.
      if (hotelSearchParams.destination && hotelSearchParams.checkin && hotelSearchParams.checkout) {
        // Drop the entire page cache — its entries are keyed without
        // a personalize bit, so re-using them would show stale scores.
        hotelPageCacheRef.current.clear()
        // Re-fetch the current page with the new flag.
        void fetchHotelsPage(hotelPage || 1, lastFiltersRef.current)
      }
    },
    [hotelSearchParams, hotelPage, fetchHotelsPage],
  )

  // VOY-461: flight toggle setter. Flights aren't paginated like hotels, so
  // this just flips the flag; the next runSearch threads it. (A live re-fetch
  // would require re-issuing the flight query — kept simple: toggle then search.)
  const setPersonalizeFlights = useCallback((next: boolean) => {
    setPersonalizeFlightsState(next)
    personalizeFlightsRef.current = next
  }, [])

  return useMemo<SearchPipelineApi>(
    () => ({
      flights,
      hotels,
      itinerary,
      hotelSuggestions,
      widenedHotels,
      hotelTotal,
      hotelPage,
      hotelPerPage: HOTEL_PER_PAGE,
      hotelTotalPages,
      hotelSearchParams,
      isSearching,
      isLoadingFlights,
      isLoadingHotels,
      isLoadingItinerary,
      isGeneratingItinerary,
      hotelRetryState,
      searchedForFlights,
      searchedForHotels,
      searchedForItinerary,
      personalizeHotels,
      setPersonalizeHotels,
      personalizeFlights,
      setPersonalizeFlights,
      runSearch,
      fetchHotelsPage,
      reset,
    }),
    [
      flights, hotels, itinerary, hotelSuggestions, widenedHotels, hotelTotal,
      hotelPage, hotelTotalPages, hotelSearchParams, isSearching,
      isLoadingFlights, isLoadingHotels, isLoadingItinerary,
      isGeneratingItinerary, hotelRetryState, searchedForFlights,
      searchedForHotels, searchedForItinerary, personalizeHotels,
      setPersonalizeHotels, personalizeFlights, setPersonalizeFlights,
      runSearch, fetchHotelsPage, reset,
    ],
  )
}

// ---------------------------------------------------------------------------
// Hotel search data builder — internal helper used by runSearch.
// Replicates the per-call logic from pre-VOY-477 search-plus.tsx
// (lines 1382-1435). Default check-in tomorrow / check-out +7d when the
// user didn't supply dates, clamp max stay to 30 nights, resolve IATA to
// city name for RateHawk's text-based search.
// ---------------------------------------------------------------------------
interface BuiltHotelSearchData {
  destination: string
  checkin: string
  checkout: string
  guests: Array<{ adults: number; children: number[] }>
}

async function buildHotelSearchData(params: RunSearchParams): Promise<BuiltHotelSearchData> {
  let destName = params.destinationName || params.destination
  if (params.destination && params.destination.length === 3) {
    try {
      const results = await searchAirportCodes(params.destination)
      if (results && results.length > 0) {
        destName = removeAirportName(results[0].city)
      }
    } catch {
      destName = params.destinationName || params.destination
    }
  }
  if (destName) destName = removeAirportName(destName)

  const msPerDay = 86_400_000
  const defaultCheckin = new Date()
  defaultCheckin.setDate(defaultCheckin.getDate() + 1)
  const ci = params.departureDate ? new Date(params.departureDate) : defaultCheckin
  // Pre-VOY-477 bug: when departureDate is set but returnDate is missing
  // (AI parser dropped the return), the old code used today+8 as
  // checkout — which falls BEFORE a future departureDate and RateHawk
  // rejects with "Check-out date must be after check-in date". Default
  // to checkin + 7 days instead, so the hotel search has a sensible
  // window relative to the resolved check-in.
  const coRaw = params.returnDate
    ? new Date(params.returnDate)
    : new Date(ci.getTime() + 7 * msPerDay)
  const nights = Math.ceil((coRaw.getTime() - ci.getTime()) / msPerDay)
  const co = nights > 30 ? new Date(ci.getTime() + 30 * msPerDay) : coRaw
  const fmt = (d: Date) => d.toISOString().split('T')[0]

  const adultCount = params.travelers?.adults ?? params.passengers?.adults ?? 2
  const childCount = params.travelers?.children ?? params.passengers?.children ?? 0
  const paramAges = params.travelers?.childrenAges ?? params.passengers?.childrenAges
  const childAgesForSearch =
    childCount === 0
      ? []
      : Array.from({ length: childCount }, (_, i) => {
          const raw = paramAges?.[i]
          return Math.min(17, Math.max(1, raw ?? 1))
        })

  return {
    // `destName` is "" only if both destinationName and destination were
    // empty strings; the runSearch caller already gates on params.destination.
    destination: destName ?? '',
    checkin: fmt(ci),
    checkout: fmt(co),
    guests: [{ adults: adultCount, children: childAgesForSearch }],
  }
}
